import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  Icon,
  SimpleGrid,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  VStack,
  Box,
  Divider,
  useToast,
} from "@chakra-ui/react";
import { FaPlus, FaListUl, FaTag, FaCalendarAlt } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const Main_Category = () => {
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [categories, setCategories] = useState([]); // State for categories
  const [newCategory, setNewCategory] = useState(""); // State for new category input
  const [eventTitle, setEventTitle] = useState(""); // State for event title selection
  const [events, setEvents] = useState([]); // State for events list
  const [isLoading, setIsLoading] = useState(false); // Loading state for API request

  // Fetch events when the component mounts
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const schoolId = sessionStorage.getItem("school_id"); // Replace with actual school_id
        const response = await axios.get(
          `${BASE_URL}/admin/events/event/list_all_event.php?school_id=${schoolId}`
        );
        console.log(response);

        if (response.data.success) {
          setEvents(response.data.data.events);
        } else {
          toast({
            title: "Error Fetching Events",
            description: `Error fetching events: ${response.data.message}`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        toast({
          title: "Error Fetching Events",
          description: "An error occurred while fetching events.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchEvents();
  }, []);

  const handleAddCategory = () => {
    if (newCategory.trim() === "") return; // Avoid empty categories
    setCategories((prevCategories) => [...prevCategories, newCategory.trim()]);
    setNewCategory(""); // Clear input field
  };

  const handleSave = async () => {
    if (!eventTitle || categories.length === 0) {
      toast({
        title: "Selection Required",
        description:
          "Please select an event title and add at least one category.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      user_id: sessionStorage.getItem("user_id"), // Replace with actual user_id
      school_id: sessionStorage.getItem("school_id"), // Replace with actual school_id
      event_id: eventTitle, // Selected event title
      categorys: categories, // List of categories
    };

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/events/category/create_category.php`,
        payload
      );

      if (response.data.success) {
        toast({
          title: "Event Categories Created Successfully",
          description: "The event categories have been created successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setCategories([]); // Clear categories
        setEventTitle(""); // Reset event title
        onClose(); // Close modal
      } else {
        toast({
          title: "Error",
          description: `Error: ${response.data.message}`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error creating categories:", error);
      toast({
        title: "Error Saving Categories",
        description: "An error occurred while saving the categories.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        marginRight={2}
        leftIcon={<FaPlus />}
        colorScheme="purple"
        onClick={onOpen}
        display={{ base: "flex", md: "inline-flex" }} // Hide text on mobile (base breakpoint)
      >
        <Box display={{ base: "none", md: "inline" }}>Category</Box>
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Add Categories For Specific Event
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <Divider mt={0} />
            {/* Event Title Dropdown */}
            <FormControl id="eventTitle" mb={4}>
              <FormLabel>
                <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                Event Title
              </FormLabel>
              <Select
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                placeholder="Select Event Title"
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
              >
                {events.length > 0 ? (
                  events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.event_title}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    No events available
                  </option>
                )}
              </Select>
            </FormControl>

            {/* Category Management Section */}
            <VStack marginTop={3} align="stretch" spacing={4}>
              <FormControl id="newCategory">
                <FormLabel>
                  <Icon as={FaTag} mr={1} color="teal.500" />
                  Add New Category
                </FormLabel>
                <Box display="flex" gap={2}>
                  <Input
                    bg="white"
                    _hover={{ borderColor: "teal.300" }}
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Enter Category Name"
                  />
                  <Button colorScheme="purple" onClick={handleAddCategory}>
                    Add
                  </Button>
                </Box>
              </FormControl>

              <Box>
                <FormLabel>
                  <Icon as={FaListUl} mr={1} color="teal.500" />
                  Added Categories
                </FormLabel>
                <VStack
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  align="stretch"
                  border="1px solid"
                  borderColor="gray.300"
                  borderRadius="md"
                  p={3}
                >
                  {categories.length > 0 ? (
                    categories.map((category, index) => (
                      <Box key={index} p={2} bg="gray.100" borderRadius="md">
                        {category}
                      </Box>
                    ))
                  ) : (
                    <Box>No categories added yet.</Box>
                  )}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              mr={3}
              onClick={handleSave}
              isLoading={isLoading}
            >
              Save
            </Button>
            {/* <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Main_Category;
